import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { Bot } from "lucide-react"
import { OnboardingEmptyState } from "@/components/System"
import { useWindowSize, useToast } from "@/hooks"
import { Workspace, Agent, AgentType, Permission } from "@/types"
import type { KnowledgeBaseFolder } from "@/hooks/api"

import { CustomizeAgentTable as AgentTable } from "./CustomizeAgentTable"
import { CustomizeAgentCard as AgentCard } from "./CustomizeAgentCard"
import { CreateDialog as AgentCreateDialog } from "./CreateDialog"
import { EditDrawer as AgentEditDrawer } from "./EditDrawer"

enum DialogCase {
  ShowBasic = "showBasic",
  Close = "close",
}

export interface CustomizeAgentsViewProps {
  agents: Agent[]
  workspace: Workspace
  knowledgeBaseMap: Map<string, KnowledgeBaseFolder>
  onRefreshAgents: () => void
  onNewAgent?: () => void
}

const CustomizeAgentsView = ({
  agents,
  workspace,
  knowledgeBaseMap,
  onRefreshAgents,
  onNewAgent,
}: CustomizeAgentsViewProps) => {
  const { t } = useTranslation()
  const { successToast } = useToast()
  const navigate = useNavigate()
  const { isMobile } = useWindowSize()

  const [editAgent, setEditAgent] = useState<Agent>()
  const [dialogCase, setDialogCase] = useState<DialogCase>(DialogCase.Close)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)

  const handleAgentPlayground = (agent: Agent) => {
    navigate({
      pathname: `/workspaces/${workspace.id}/agents/${agent.id}`,
    })
  }

  const handleDeleteAgent = () => {
    onRefreshAgents?.()
  }

  const handleCreatedAgent = (agent: Agent) => {
    setEditAgent(undefined)
    successToast({
      title: t("workspace.agent.message.createSuccess.title"),
      description: t("workspace.agent.message.createSuccess.description", {
        name: agent.name,
      }),
    })
    onRefreshAgents?.()
  }

  const handleUpdatedAgent = (agent: Agent) => {
    setEditAgent(undefined)
    successToast({
      title: t("workspace.agent.message.updateSuccess.title"),
      description: t("workspace.agent.message.updateSuccess.description", {
        name: agent.name,
      }),
    })
    onRefreshAgents?.()
  }

  return (
    <>
      {agents && agents.length === 0 ? (
        <OnboardingEmptyState
          icon={Bot}
          title={t("workspace.agentTab.onboarding.title")}
          description={t("workspace.agentTab.onboarding.description")}
          steps={[
            { title: t("workspace.agentTab.onboarding.steps.configure") },
            { title: t("workspace.agentTab.onboarding.steps.connect") },
            { title: t("workspace.agentTab.onboarding.steps.test") },
          ]}
          primaryAction={
            onNewAgent
              ? {
                  label: t("workspace.new"),
                  onClick: onNewAgent,
                  permission: {
                    required: Permission.WsUpdateResources,
                    workspace,
                    disabledTooltip: t("common.noPermission"),
                  },
                }
              : undefined
          }
        />
      ) : (
        agents && (
          <>
            {isMobile ? (
              <div className="flex flex-col gap-4">
                {agents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    workspace={workspace}
                    agent={agent}
                    knowledgeBaseMap={knowledgeBaseMap}
                    onPlayground={handleAgentPlayground}
                    onDelete={handleDeleteAgent}
                  />
                ))}
              </div>
            ) : (
              <AgentTable
                workspace={workspace}
                agents={agents}
                knowledgeBaseMap={knowledgeBaseMap}
                onPlayground={handleAgentPlayground}
                onDelete={handleDeleteAgent}
              />
            )}
          </>
        )
      )}

      {/* dialog */}
      <AgentCreateDialog
        agentType={AgentType.Basic}
        workspace={workspace}
        open={dialogCase !== DialogCase.Close}
        knowledgeBaseMap={knowledgeBaseMap}
        onOpenChange={(open) => {
          if (!open) {
            setDialogCase(DialogCase.Close)
          }
        }}
        onConfirm={handleCreatedAgent}
      />
      {editAgent && (
        <AgentEditDrawer
          agent={editAgent}
          workspace={workspace}
          open={editDrawerOpen}
          knowledgeBaseMap={knowledgeBaseMap}
          onOpenChange={(open) => {
            if (!open) {
              setEditDrawerOpen(false)
            }
          }}
          onUpdated={handleUpdatedAgent}
        />
      )}
    </>
  )
}

export default CustomizeAgentsView
